import { instance } from '../apiBase/instance';

export const AppApc = {
    GetApcListTransaction() {
        return instance.get('wallet/deposits');
    },
    GetDepositOption() {
        return instance.get('wallet');
    },
    GetListMyVoucher() {
        return instance.get('vouchers');
    },
    GetCategories() {
        return instance.get('categories');
    },
    GetRegions() {
        return instance.get('regions');
    },
    GetProducts(
        category: string,
        region: string,
        query: string,
        page: number,
        limit: number,
        catalog?: string
    ) {
        return instance.get('products', {
            params: {
                category,
                region,
                query,
                page,
                limit,
                ...(catalog ? { catalog } : {}),
            },
        });
    },
    GetCatalogs() {
        return instance.get('catalogs');
    },
    GetProduct(id: string) {
        return instance.get(`products/${id}`);
    },
    PurchaseProduct(params: {
        productId: string;
        faceValue: number;
        faceCurrency: string;
        quantity: number;
        cashbackVoucherCode?: string;
    }) {
        return instance.post('vouchers/quotes', params);
    },
    BuyPurchaseVoucher(params: {
        quoteId: string;
        acceptedTermsVersion: string;
        recipientEmail: string;
    }) {
        return instance.post('vouchers/orders', params);
    },
    GetOrderDetail(orderId: string) {
        return instance.get(`vouchers/orders?id=${orderId}`);
    },
    GetVoucherOrder(voucherId: string) {
        return instance.post(`vouchers/reveal?id=${voucherId}`);
    },
    UpdateVoucherUsage(voucherId: string, status: 'USED' | 'READY') {
        return instance.post(`vouchers/usage?id=${voucherId}`, { status });
    },
    GetlistOrders() {
        return instance.get('vouchers/orders');
    },
    GetCashbackAwards() {
        return instance.get('cashback-awards');
    },
    GetNotificationList() {
        return instance.get('notifications');
    },
    MarkNotificationAsRead(params: { id: string }) {
        return instance.post(`notifications/read`, params);
    },
    WithdrawFund(params: { amount: string; toAddress: string; idempotencyKey: string }) {
        return instance.post('wallet/withdraws', params);
    },
    ListWithdrawHistory() {
        return instance.get('wallet/withdraws');
    },
    GetConfig(key: string) {
        return instance.get(`setting-config/?key=${key}`);
    },

    SearchCirclePeople(q: string) {
        return instance.get<ApiEnvelope<CirclePerson[]>>('circles/people', { params: { q } });
    },
    CreateCircle(params: CreateCirclePayload) {
        return instance.post<ApiEnvelope<CircleDetail>>('circles', params);
    },
    UpdateCircle(params: UpdateCirclePayload) {
        return instance.post<ApiEnvelope<CircleDetail>>('circles/update', params);
    },
    GetCircles(params?: CircleListQuery) {
        return instance.get<ApiEnvelope<CircleSummary[]>>('circles', { params });
    },
    GetCircle(id: string) {
        return instance.get<ApiEnvelope<CircleDetail>>('circles', { params: { id } });
    },
    AddCircleMember(params: CircleMemberPayload) {
        return instance.post<ApiEnvelope<CircleDetail>>('circles/members', params);
    },
    RemoveCircleMember(params: CircleMemberPayload) {
        return instance.post<ApiEnvelope<CircleDetail>>('circles/members/remove', params);
    },
    InviteCircleMember(params: CircleMemberPayload) {
        return instance.post<ApiEnvelope<CircleDetail>>('circles/invites', params);
    },
    GetCircleInvites() {
        return instance.get<ApiEnvelope<CircleInvite[]>>('circles/invites');
    },
    RespondCircleInvite(params: RespondCircleInvitePayload) {
        return instance.post<ApiEnvelope<CircleDetail>>('circles/invites/respond', params);
    },

    CreateRedPacket(params: CreateRedPacketPayload) {
        return instance.post<ApiEnvelope<RedPacket>>('red-packets', params);
    },
    GetRedPackets(params?: RedPacketListQuery) {
        return instance.get<ApiEnvelope<RedPacket[]>>('red-packets', { params });
    },
    GetRedPacket(id: string) {
        return instance.get<ApiEnvelope<RedPacket>>('red-packets', { params: { id } });
    },
    GetRedPacketByToken(token: string) {
        return instance.get<ApiEnvelope<RedPacket>>('red-packets', { params: { token } });
    },
    ClaimRedPacket(params: RedPacketIdPayload) {
        return instance.post<ApiEnvelope<RedPacket>>('red-packets/claims', params);
    },
    CancelRedPacket(params: RedPacketIdPayload) {
        return instance.post<ApiEnvelope<RedPacket>>('red-packets/cancel', params);
    },
    GetRedPacketCreateLogs(params?: RedPacketCreateLogQuery) {
        return instance.get<ApiEnvelope<RedPacketCreateLog[]>>('red-packets/create-logs', { params });
    },
    GetRedPacketClaimLogs(params?: RedPacketClaimLogQuery) {
        return instance.get<ApiEnvelope<RedPacketClaimLog[]>>('red-packets/claim-logs', { params });
    },
};

export type ApiEnvelope<T> = {
    success: boolean;
    data: T;
    message?: string;
    count?: number;
    meta?: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
};

export type CirclePerson = {
    id: string;
    displayName: string;
    email: string;
};

export type CircleMemberStatus = 'ACTIVE' | 'PENDING';

export type CircleMember = {
    id: string;
    userId: string;
    displayName: string;
    status: CircleMemberStatus;
};

export type CircleSummary = {
    id: string;
    name: string;
    isShared: boolean;
    ownerUserId: string;
    isOwner: boolean;
    eligibleCount: number;
    pendingCount: number;
    createdAt: string;
};

export type CircleDetail = CircleSummary & {
    members: CircleMember[];
};

export type CircleInvite = {
    id: string;
    circleId: string;
    circleName: string;
    createdAt: string;
};

export type CircleListQuery = {
    page?: number;
    limit?: number;
    owned?: boolean;
    id?: string;
};

export type CreateCirclePayload = {
    name: string;
    isShared?: boolean;
    memberUserIds?: string[];
};

export type UpdateCirclePayload = {
    id: string;
    name?: string;
    isShared?: boolean;
};

export type CircleMemberPayload = {
    circleId: string;
    userId: string;
};

export type RespondCircleInvitePayload = {
    id: string;
    accept: boolean;
};

export type RedPacketSplit = 'EQUAL' | 'RANDOM';
export type RedPacketAudience = 'LINK' | 'CIRCLE';
export type RedPacketTemplate =
    | 'CLASSIC'
    | 'APC_MINT'
    | 'MIDNIGHT'
    | 'BIRTHDAY'
    | 'LUNAR_NEW_YEAR'
    | 'THANK_YOU'
    | 'TEAM_CHEERS';

export type RedPacketStatus =
    | 'FUNDING'
    | 'ACTIVE'
    | 'FULLY_CLAIMED'
    | 'EXPIRED'
    | 'CANCELLED'
    | 'FAILED';

export type RedPacketClaimStatus =
    | 'ALLOCATED'
    | 'PAYOUT_SENT'
    | 'CREDITED'
    | 'FAILED_RETRYABLE';

export type RedPacketListScope = 'mine' | 'inbox' | 'all';
export type RedPacketListStatus = 'openable' | 'expired' | 'fully_claimed';

export type RedPacketListQuery = {
    scope?: RedPacketListScope;
    status?: RedPacketListStatus;
    page?: number;
    limit?: number;
};

export type CreateRedPacketPayload = {
    amount: string;
    split: RedPacketSplit;
    idempotencyKey: string;
    audience?: RedPacketAudience;
    receiverCount?: number;
    circleId?: string;
    eligibleUserIds?: string[];
    template?: RedPacketTemplate;
    expiresInHours?: number;
    message?: string;
};

export type RedPacketIdPayload = {
    id: string;
};

export type RedPacketClaim = {
    status: RedPacketClaimStatus;
    amount?: string;
};

export type RedPacketRecipient = {
    userId: string;
    displayName: string;
};

export type FundingLegStatus = 'PLANNED' | 'SWAPPING' | 'READY' | 'FAILED';

export type RedPacketFundingLeg = {
    chain: string;
    asset: string;
    amount: string;
    needsSwap: boolean;
    status: FundingLegStatus;
    txHash?: string | null;
    usdcReceived?: string | null;
    failureReason?: string | null;
};

export type RedPacket = {
    id: string;
    shareToken: string;
    creatorUserId: string;
    creatorEmail: string;
    creatorDisplayName: string;
    totalAmount: string;
    feeAmount: string;
    debitAmount: string;
    receiverCount: number;
    claimedCount: number;
    remainingCount: number;
    split: RedPacketSplit;
    audience: RedPacketAudience;
    circleId?: string | null;
    circleName?: string | null;
    eligibleCount?: number;
    eligibleUserIds?: string[];
    eligibleRecipients?: RedPacketRecipient[];
    canClaim: boolean;
    template: RedPacketTemplate;
    message: string;
    status: RedPacketStatus;
    failureReason: string | null;
    settlementChain: string;
    settlementAsset: string;
    expiresAt: string;
    createdAt: string;
    myClaim: RedPacketClaim | null;
    claims: RedPacketClaim[];
    balances: unknown[];
    fundingLegs: RedPacketFundingLeg[];
};

export type RedPacketCreateLogEvent = 'CREATED' | 'FAILED' | 'CREATE_REJECTED' | 'CREATE_FAILED';

export type RedPacketClaimLogEvent =
    | 'CLAIMED'
    | 'FAILED'
    | 'REJECTED'
    | 'ALLOCATED'
    | 'PAYOUT_SENT'
    | 'CREDITED'
    | 'PAYOUT_FAILED';

export type RedPacketCreateLogQuery = {
    packetId?: string;
    event?: RedPacketCreateLogEvent;
    page?: number;
    limit?: number;
};

export type RedPacketClaimLogQuery = {
    packetId?: string;
    claimId?: string;
    event?: RedPacketClaimLogEvent;
    page?: number;
    limit?: number;
};

export type RedPacketCreateLog = {
    id: string;
    packetId: string | null;
    actorUserId: string;
    email: string;
    event: RedPacketCreateLogEvent;
    amount: string;
    message: string | null;
    createdAt: string;
};

export type RedPacketClaimLog = {
    id: string;
    packetId: string;
    claimId: string | null;
    actorUserId: string;
    email: string;
    event: RedPacketClaimLogEvent;
    amount: string;
    payoutTxHash: string | null;
    message: string | null;
    createdAt: string;
};
